// src/components/financials/ExpenseBatchImport.js
// Updated: 02/13/2026 - Added "Undo Last Import" Safety Feature

import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  Table, TableBody, TableCell, TableHead, TableRow, 
  IconButton, Alert, LinearProgress, Chip, TextField, MenuItem,
  Typography 
} from '@mui/material';
import { CloudUpload, Delete, Save, Download, Restore } from '@mui/icons-material'; // Added Restore icon
import Papa from 'papaparse';
import { db } from '../../services/firebase';
import { 
  writeBatch, collection, doc, serverTimestamp, increment, 
  getDocs, query, where // Added query and where
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const ExpenseBatchImport = ({ funds, onSuccess }) => {
  const { userProfile, currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Metadata State
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);

  const churchId = userProfile?.churchId;

  useEffect(() => {
    if (open && churchId) {
      fetchMetadata();
    }
  }, [open, churchId]);

  const fetchMetadata = async () => {
    try {
      const vSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'vendors'));
      setVendors(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const cSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'expenseCategories'));
      setCategories(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Error fetching metadata for import:", e);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setData([]);
    setError('');
    setSuccessMsg('');
  };

  const handleDownloadTemplate = () => {
    const csvContent = "Date,Payee,Amount,Category,Fund,Description,Payment Method,Check Number\n01/15/2025,Dominion Energy,150.00,Utilities,General,Monthly electric bill,Online,\n1/20/25,Office Depot,45.99,Office Supplies,Admin Fund,Printer paper and ink,Check,1045";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "expense_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    const cleanStr = dateStr.trim();
    const parts = cleanStr.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    
    if (parts) {
      const month = parts[1].padStart(2, '0');
      const day = parts[2].padStart(2, '0');
      let year = parts[3];
      if (year.length === 2) { year = '20' + year; }
      return `${year}-${month}-${day}`;
    }
    
    const d = new Date(cleanStr);
    if (!isNaN(d.getTime())) {
      return d.toISOString().split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  };

  const findMatchingFund = (inputName) => {
      if (!inputName) return null;
      const lowerInput = inputName.toLowerCase().trim();

      let match = funds.find(f => f.name.toLowerCase() === lowerInput);
      if (match) return match;

      const cleanInput = lowerInput.replace(/\s+fund$/, ''); 
      match = funds.find(f => f.name.toLowerCase().replace(/\s+fund$/, '') === cleanInput);
      
      return match;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setSuccessMsg('');

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(), 
      complete: (results) => {
        const parsed = results.data.map((row, index) => {
          
          const fundName = row['fund'] || row['account'] || '';
          const matchedFund = findMatchingFund(fundName);
          
          const rawAmount = row['amount'] || row['debit'] || row['withdrawal'] || row['total'] || '0';
          const cleanAmount = rawAmount.replace(/[^0-9.-]+/g, "");
          const finalAmount = Math.abs(parseFloat(cleanAmount) || 0).toFixed(2);
          
          return {
            id: index, 
            date: parseDate(row['date']), 
            payee: (row['payee'] || row['vendor'] || row['name'] || row['description'] || 'Unknown').trim(),
            amount: finalAmount, 
            category: (row['category'] || row['type'] || 'Uncategorized').trim(),
            fundId: matchedFund ? matchedFund.id : '', 
            fundName: fundName,
            description: row['description'] || row['memo'] || '',
            paymentMethod: row['payment method'] || row['method'] || 'Other',
            checkNumber: row['check number'] || row['check #'] || '', 
          };
        });
        setData(parsed);
      },
      error: (err) => setError("Error parsing CSV: " + err.message)
    });
  };

  const handleFundChange = (index, newFundId) => {
    const newData = [...data];
    newData[index].fundId = newFundId;
    setData(newData);
  };

  const handleDeleteRow = (index) => {
    setData(data.filter((_, i) => i !== index));
  };

  const normalize = (str) => str ? str.toLowerCase().trim() : '';

  // --- NEW UNDO FUNCTION ---
  const handleUndoLastImport = async () => {
    if (!window.confirm("Are you sure you want to undo the most recent mass import? This will delete the imported expenses and restore the exact amounts back to your fund balances.")) return;
    
    setLoading(true);
    setError('');
    setSuccessMsg('');
    
    try {
        // 1. Fetch all expenses tagged as mass imports
        const q = query(
            collection(db, 'churches', churchId, 'financials', 'data', 'expenses'),
            where('isPastImport', '==', true)
        );
        
        const snap = await getDocs(q);
        if (snap.empty) {
            setError("No recent imports found to undo.");
            setLoading(false);
            return;
        }

        // 2. Sort in memory to find the most recent batch ID (avoids needing Firebase composite indexes)
        const importedDocs = snap.docs.map(d => ({ ref: d.ref, ...d.data() }));
        importedDocs.sort((a, b) => (b.importedAt?.toMillis() || 0) - (a.importedAt?.toMillis() || 0));
        
        const latestBatchId = importedDocs[0].batchImportId;
        if (!latestBatchId) throw new Error("Could not locate a valid Batch ID on the recent imports.");

        // Filter to ONLY the docs from that exact batch
        const docsToUndo = importedDocs.filter(d => d.batchImportId === latestBatchId);

        // 3. Calculate how much money needs to be returned to each fund
        const fundRefunds = {};
        docsToUndo.forEach(doc => {
            if (!fundRefunds[doc.fundId]) fundRefunds[doc.fundId] = 0;
            fundRefunds[doc.fundId] += parseFloat(doc.amount);
        });

        const batch = writeBatch(db);
        
        // 4. Queue Expense Deletions
        docsToUndo.forEach(doc => {
            batch.delete(doc.ref);
        });

        // 5. Queue Fund Refunds (Adding money back)
        Object.keys(fundRefunds).forEach(fundId => {
            const fundRef = doc(db, 'churches', churchId, 'financials', 'data', 'funds', fundId);
            batch.update(fundRef, { balance: increment(fundRefunds[fundId]) });
        });

        // 6. Commit the entire rollback at once
        await batch.commit();
        setSuccessMsg(`Undo successful! Deleted ${docsToUndo.length} expenses and restored fund balances.`);
        if (onSuccess) onSuccess();

    } catch (e) {
        console.error(e);
        setError("Error undoing import: " + e.message);
    }
    setLoading(false);
  };

  const handleImport = async () => {
    if (data.length === 0) return;
    
    const validRows = data.filter(r => r.fundId && parseFloat(r.amount) > 0);
    if (validRows.length === 0) {
        if (data.length > 0) {
            return setError("No valid rows to import. Please ensure all rows have an Amount greater than $0 and a Fund is selected.");
        }
        return setError("No valid rows to import.");
    }
    
    if (data.length !== validRows.length) {
       if(!window.confirm(`Skipping ${data.length - validRows.length} invalid rows (likely missing a Fund selection). Continue?`)) return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(db);
      const expenseColRef = collection(db, 'churches', churchId, 'financials', 'data', 'expenses');
      const fundColRef = collection(db, 'churches', churchId, 'financials', 'data', 'funds');
      const vendorColRef = collection(db, 'churches', churchId, 'financials', 'data', 'vendors');
      const categoryColRef = collection(db, 'churches', churchId, 'financials', 'data', 'expenseCategories');
      
      const existingExpSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'expenses'));
      const existingSignatures = new Set(
          existingExpSnap.docs.map(d => {
              const data = d.data();
              return `${data.date}|${normalize(data.payee)}|${parseFloat(data.amount).toFixed(2)}`;
          })
      );

      const fundImpacts = {}; 
      const newVendorsToCreate = new Set();
      const newCategoriesToCreate = new Set();
      
      const batchImportId = `import_${Date.now()}`;
      let importedCount = 0;
      let skippedCount = 0;

      const localVendors = [...vendors];
      const localCategories = [...categories];

      for (const row of validRows) {
          const amount = parseFloat(row.amount);
          const signature = `${row.date}|${normalize(row.payee)}|${amount.toFixed(2)}`;

          if (existingSignatures.has(signature)) {
              skippedCount++;
              continue; 
          }

          let vendorId = 'unknown';
          let vendorName = row.payee;
          const matchedVendor = localVendors.find(v => normalize(v.name) === normalize(row.payee));
          
          if (matchedVendor) {
              vendorId = matchedVendor.id;
              vendorName = matchedVendor.name; 
          } else if (!newVendorsToCreate.has(normalize(row.payee))) {
              const newVendorRef = doc(vendorColRef);
              batch.set(newVendorRef, {
                  name: row.payee,
                  defaultCategory: row.category,
                  createdAt: serverTimestamp()
              });
              vendorId = newVendorRef.id;
              localVendors.push({ id: vendorId, name: row.payee });
              newVendorsToCreate.add(normalize(row.payee));
          } else {
              const tempV = localVendors.find(v => normalize(v.name) === normalize(row.payee));
              if(tempV) vendorId = tempV.id;
          }

          let categoryName = row.category;
          const matchedCategory = localCategories.find(c => normalize(c.name) === normalize(row.category));
          
          if (matchedCategory) {
              categoryName = matchedCategory.name;
          } else if (!newCategoriesToCreate.has(normalize(row.category))) {
              const newCatRef = doc(categoryColRef);
              batch.set(newCatRef, {
                  name: row.category,
                  createdAt: serverTimestamp()
              });
              localCategories.push({ id: newCatRef.id, name: row.category });
              newCategoriesToCreate.add(normalize(row.category));
          }

          const docRef = doc(expenseColRef);
          batch.set(docRef, {
               date: row.date,
               payee: vendorName,
               vendorId: vendorId,
               amount: amount,
               category: categoryName,
               fundId: row.fundId,
               description: row.description,
               paymentMethod: row.paymentMethod,
               checkNumber: row.checkNumber || '', 
               status: 'approved', 
               reconciled: false, 
               batchImportId: batchImportId, 
               importedAt: serverTimestamp(),
               importedBy: currentUser.uid,
               isPastImport: true,
               createdAt: serverTimestamp(),
               createdBy: currentUser.uid
          });

          if (!fundImpacts[row.fundId]) fundImpacts[row.fundId] = 0;
          fundImpacts[row.fundId] += amount;

          existingSignatures.add(signature);
          importedCount++;
      }

      if (importedCount === 0) {
          setError(`No records imported. ${skippedCount} duplicate(s) skipped.`);
          setLoading(false);
          return;
      }

      Object.keys(fundImpacts).forEach(fundId => {
          const totalSubtract = fundImpacts[fundId];
          const fundRef = doc(fundColRef, fundId);
          batch.update(fundRef, { 
              balance: increment(-totalSubtract) 
          });
      });

      await batch.commit();
      
      const resultMsg = `Import Complete! Added ${importedCount} expenses.` + 
                        (skippedCount > 0 ? ` Skipped ${skippedCount} duplicates.` : '') +
                        (newVendorsToCreate.size > 0 ? ` Created ${newVendorsToCreate.size} new vendors.` : '');
      
      setSuccessMsg(resultMsg);
      if (onSuccess) onSuccess();
      
      setData([]);

    } catch (e) {
      setError("Import failed: " + e.message);
    }
    setLoading(false);
  };

  return (
    <>
      <Button variant="outlined" startIcon={<CloudUpload />} onClick={handleOpen} sx={{ ml: 1 }}>
        Import CSV
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogTitle>Mass Import Past Expenses</DialogTitle>
        <DialogContent dividers>
          
          {successMsg ? (
              <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>
          ) : (
            <>
              {/* Added the Undo Last Import Button here */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant="contained" component="label" startIcon={<CloudUpload />}>
                  Upload CSV
                  <input type="file" hidden accept=".csv" onChange={handleFileUpload} />
                </Button>
                <Button variant="text" startIcon={<Download />} onClick={handleDownloadTemplate}>
                  Download Template
                </Button>
                <Button variant="text" color="warning" startIcon={<Restore />} onClick={handleUndoLastImport} disabled={loading}>
                  Undo Last Import
                </Button>
                {data.length > 0 && <Chip label={`${data.length} rows loaded`} color="primary" />}
              </Box>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  * Smart Matching: "General" will automatically match "General Fund". <br/>
                  * Duplicates (same Date, Payee, and Amount) will be automatically skipped.<br/>
                  * Check Numbers are optional.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {loading && <LinearProgress sx={{ mb: 2 }} />}

              {data.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Payee</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Fund (Required)</TableCell>
                      <TableCell>Check #</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row, idx) => {
                      const isVendorNew = !vendors.find(v => normalize(v.name) === normalize(row.payee));
                      const isCatNew = !categories.find(c => normalize(c.name) === normalize(row.category));

                      return (
                      <TableRow key={idx} sx={{ bgcolor: !row.fundId ? '#fff3e0' : 'inherit' }}>
                        <TableCell>
                            <TextField 
                                type="date"
                                size="small"
                                value={row.date}
                                onChange={(e) => {
                                    const newData = [...data];
                                    newData[idx].date = e.target.value;
                                    setData(newData);
                                }}
                            />
                        </TableCell>
                        <TableCell>
                            {row.payee}
                            {isVendorNew && <Chip size="small" label="New" color="info" sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}/>}
                        </TableCell>
                        <TableCell>${row.amount}</TableCell>
                        <TableCell>
                            {row.category}
                            {isCatNew && <Chip size="small" label="New" color="info" sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}/>}
                        </TableCell>
                        <TableCell>
                          <TextField 
                            select 
                            size="small" 
                            value={row.fundId} 
                            onChange={(e) => handleFundChange(idx, e.target.value)}
                            error={!row.fundId}
                            sx={{ minWidth: 150 }}
                          >
                            <MenuItem value=""><em>Select Fund...</em></MenuItem>
                            {funds.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                           <TextField 
                              size="small"
                              placeholder="Optional"
                              value={row.checkNumber}
                              onChange={(e) => {
                                  const newData = [...data];
                                  newData[idx].checkNumber = e.target.value;
                                  setData(newData);
                              }}
                              sx={{ maxWidth: 100 }}
                           />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => handleDeleteRow(idx)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{successMsg ? "Close" : "Cancel"}</Button>
          {!successMsg && (
              <Button 
                variant="contained" 
                color="success" 
                onClick={handleImport} 
                disabled={loading || data.length === 0}
                startIcon={<Save />}
              >
                Complete Import
              </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpenseBatchImport;